import { useCallback, useEffect, useRef } from "react";
import { useWebSocket, type Operation } from "../lib/useWebSocket";
import { getContactCounts } from "../lib/contacts";
import type { EdgeData, GraphData, NodeData, Structure } from "../lib/api";

type UseCollaborationParams = {
  structureId?: string;
  structure: Structure | null;
  shareToken?: string;
  viewOnlyMode: boolean;
  mapGraphState: (nodes: any[], edges: any[]) => { nodes: any[]; edges: any[] };
  resolveContacts: (node: NodeData, lookupOverride?: Record<string, any>) => any[];
  toggleCollapse: (id: string) => void;
  setRfNodes: (nodes: any[]) => void;
  setRfEdges: (edges: any[]) => void;
  rfStateRef: React.MutableRefObject<{ nodes: any[]; edges: any[] }>;
  recordHistory: (mode: "immediate" | "debounced", nodes?: any[], edges?: any[]) => void;
  markRemote: () => void;
  setDraftDirty: (v: boolean) => void;
  wsAuthToken: string | null;
  wsShareToken: string | null;
  wsRef: React.MutableRefObject<any>;
};

const normalizeHandleId = (handle?: string | null) => {
  if (!handle) return undefined;
  const h = String(handle).toLowerCase();
  const map: Record<string, string> = {
    "top-in": "top",
    "top-out": "top",
    "bottom-in": "bottom",
    "bottom-out": "bottom",
    "left-in": "left",
    "left-out": "left",
    "right-in": "right",
    "right-out": "right",
  };
  if (map[h]) return map[h];
  if (["top", "bottom", "left", "right"].includes(h)) return h;
  return undefined;
};

export function useCollaboration({
  structureId,
  structure,
  shareToken,
  viewOnlyMode,
  mapGraphState,
  resolveContacts,
  toggleCollapse,
  setRfNodes,
  setRfEdges,
  rfStateRef,
  recordHistory,
  markRemote,
  setDraftDirty,
  wsAuthToken,
  wsShareToken,
  wsRef,
}: UseCollaborationParams) {
  const handleWebSocketOperation = useCallback(
    (op: Operation) => {
      const { nodes: currentNodes, edges: currentEdges } = rfStateRef.current;
      let nodes = currentNodes;
      let edges = currentEdges;

      if (op.type === "node_moved") {
        nodes = currentNodes.map((n) =>
          n.id === op.node_id
            ? { ...n, position: { x: op.x, y: op.y }, data: { ...(n.data as NodeData), x: op.x, y: op.y } }
            : n
        );
      } else if (op.type === "node_updated") {
        nodes = currentNodes.map((n) => {
          if (n.id !== op.node_id) return n;
          const merged = { ...(n.data as NodeData), ...op.patch };
          const contactIds = (merged as any).contact_ids || [];
          const contacts = resolveContacts(merged);
          const { board: boardContactsCount, group: groupContactsCount } = getContactCounts(contacts as any);
          return {
            ...n,
            data: {
              ...merged,
              contact_ids: contactIds,
              contacts,
              contactsCount: contacts.length || contactIds.length,
              boardContactsCount,
              groupContactsCount,
            },
          };
        });
      } else if (op.type === "node_added") {
        const contactIds = (op.node as any).contact_ids || [];
        const contacts = resolveContacts(op.node as NodeData);
        const { board: boardContactsCount, group: groupContactsCount } = getContactCounts(contacts as any);
        const newNode = {
          id: op.node.id,
          type: op.node.shape || "atlas",
          position: { x: op.node.x || 0, y: op.node.y || 0 },
          data: {
            ...op.node,
            contact_ids: contactIds,
            contacts,
            contactsCount: contacts.length || contactIds.length,
            boardContactsCount,
            groupContactsCount,
            onToggleCollapse: () => toggleCollapse(op.node.id),
          },
          draggable: !viewOnlyMode,
          selectable: !viewOnlyMode,
        };
        nodes = [...currentNodes, newNode];
      } else if (op.type === "node_deleted") {
        nodes = currentNodes.filter((n) => n.id !== op.node_id);
      } else if (op.type === "edge_added") {
        const newEdge = {
          id: op.edge.id,
          source: op.edge.source,
          target: op.edge.target,
          sourceHandle: normalizeHandleId(op.edge.source_handle),
          targetHandle: normalizeHandleId(op.edge.target_handle),
          type: op.edge.line_style === "step" ? "orthogonal" : op.edge.line_style === "straight" ? "straight" : "default",
          data: {
            arrow_style: op.edge.arrow_style || "default",
            line_style: op.edge.line_style || "bezier",
            routing: op.edge.routing || { mode: "auto", bendOffset: 0 },
          },
        };
        edges = [...currentEdges, newEdge];
      } else if (op.type === "edge_deleted") {
        edges = currentEdges.filter((e) => e.id !== op.edge_id);
      } else if (op.type === "edge_updated") {
        edges = currentEdges.map((e) => (e.id === op.edge_id ? { ...e, data: { ...(e.data as any), ...op.patch } } : e));
      } else if (op.type === "node_collapsed") {
        nodes = currentNodes.map((n) =>
          n.id === op.node_id ? { ...n, data: { ...(n.data as NodeData), collapsed: op.collapsed } } : n
        );
      }

      const mapped = mapGraphState(nodes, edges);
      setRfNodes(mapped.nodes);
      setRfEdges(mapped.edges);
      rfStateRef.current = { nodes: mapped.nodes, edges: mapped.edges };
      recordHistory("immediate", mapped.nodes, mapped.edges);
      markRemote();
      setDraftDirty(false);
    },
    [mapGraphState, markRemote, recordHistory, resolveContacts, setRfEdges, setRfNodes, toggleCollapse, viewOnlyMode]
  );

  const ws = useWebSocket({
    structureId: structureId || "",
    enabled: !!structureId && !!structure,
    onOperation: handleWebSocketOperation,
    authToken: wsAuthToken,
    shareToken: wsShareToken,
  });

  const wsStoreRef = useRef(ws);
  useEffect(() => {
    wsRef.current = ws;
    wsStoreRef.current = ws;
  }, [ws, wsRef]);

  return ws;
}
